/**
 * We'll use this hook in react functional components to detect and report prop changes.
 * 
 * In it's current form, it needs to be inserted at compile time as opposed to being enabled at runtime.
 *
 * @author Timir Hazarika <thazarik@cisco.com>
 *
 * @copyright Cisco Systems, Inc. All rights reserved.
 */
import { useRef } from "react";
import { getCircularReplacer } from "./templateUtils";

// best effort stringification
const stringify = (obj: any): string => JSON.stringify(obj, getCircularReplacer(), 0);

// compare two values by stringifying them
const compareStringified = (a: any, b: any): boolean => stringify(a) === stringify(b);


export const useChangeDebugger = (nextProps: any, componentName: string, strict: boolean = true): [any] => {
    const prevPropsRef = useRef(nextProps);
    const prevProps = prevPropsRef.current;
    const prevPropsKeys = Object.keys(prevProps);
    const nextPropsKeys = Object.keys(nextProps);
    const allKeys = Array.from(new Set([...prevPropsKeys, ...nextPropsKeys]));
    const changes: any = {};
    
    allKeys.forEach((key) => {
        if (prevProps[key] !== nextProps[key]) {
            if (!strict && compareStringified(prevProps[key], nextProps[key])) {
                // here we're overlooking reference changes as long as the values are the same
                return;
            }
            changes[key] = {
                prev: prevProps[key],
                next: nextProps[key],
            };
        }
    });
    
    if (Object.keys(changes).length > 0) {
        console.group(`Changes detected in ${componentName}`);
        console.log('Changes:', changes);
        console.groupEnd();
    }
    prevPropsRef.current = nextProps;
    return [changes];
};